import { cloneDeep } from 'lodash-es';
import {
  ContentText,
  ContentColumns,
  ContentStack,
  ContentTable,
  TableCell,
  TDocumentDefinitions,
  Column,
  Content,
} from 'pdfmake/interfaces';
import { imageUrlToDataUrl } from '@pedix-workspace/shared-pdf';
import {
  ArcaBuyerData,
  ArcaHeadingData,
  ArcaSellerData,
  ArcaVoucherLetter,
  ArcaLineItemsByOrder,
  ArcaVoucherAssocInfo,
  TaxVoucherArca,
  TaxVoucherSummaryArca,
} from './arca.types';
import {
  getArcaBusinessVatConditionName,
  getArcaDocumentTypeName,
  getArcaVatConditionName,
  getDocumentName,
  getDocumentTypeCode,
  getVoucherLetter,
} from './arca.utils';
import { format } from 'date-fns';
import { TaxVoucherOptions } from '@pedix-workspace/utils';
import {
  determineRenderingStrategy,
  getFormattedCaeExpirationDate,
  getArcaVoucherQrUrl,
  processLineItemsByOrder,
} from './arca-voucher.utils';

export type GenerateArcaVoucherPdfDefinitionParams = {
  taxVoucher: TaxVoucherArca;
  options: {
    watermarkText?: string;
    currencyFormatFn: (amount: number) => string;
  };
};

export async function generateArcaVoucherPdf({
  taxVoucher,
  options,
}: GenerateArcaVoucherPdfDefinitionParams) {
  const voucherLetter = getVoucherLetter(taxVoucher.details.type);
  const taxVoucherOptions: TaxVoucherOptions = {
    groupingConceptName: '',
    groupByOrder: false,
    printCopies: 'single',
    ...taxVoucher.details.taxVoucherOptions,
  };

  // Heading
  const headingContent = createHeadingContent({
    documentName: getDocumentName(taxVoucher.details.type),
    voucherLetter,
    documentTypeCode: getDocumentTypeCode(taxVoucher.details.type),
    documentNumber: taxVoucher.details.voucherRef,
    created: taxVoucher.details.created,
  });

  // Seller information
  const sellerContent = await createSellerContent({
    logoUrl: taxVoucher.details.sellerInfoExtra.logoUrl,
    fantasyName: taxVoucher.details.sellerInfoExtra.fantasyName,
    companyName: taxVoucher.details.accountDetails.companyName,
    address: taxVoucher.details.accountDetails.address,
    vatCondition: getArcaBusinessVatConditionName(taxVoucher.details.accountDetails.vatCondition),
    cuit: taxVoucher.details.accountDetails.cuit,
    operationsStartDate: taxVoucher.details.accountDetails.operationsStartDate,
    grossIncome: taxVoucher.details.accountDetails.grossIncome,
  });

  // Buyer information
  const buyerContent = createBuyerContent({
    vatCondition: getArcaVatConditionName(taxVoucher.details.buyerBillingInfo.vatCondition),
    name: taxVoucher.details.buyerBillingInfo.name,
    address: taxVoucher.details.buyerBillingInfo.address,
    documentType: taxVoucher.details.buyerBillingInfo.documentType,
    documentNumber: taxVoucher.details.buyerBillingInfo.documentNumber,
  });

  // Voucher associated info
  const voucherAssocContent =
    taxVoucher.details.operation === 'credit' && taxVoucher.details.associatedTaxVoucherSummary
      ? createVoucherAssocInfo({
          voucherLetter: getVoucherLetter(
            (<TaxVoucherSummaryArca>taxVoucher.details.associatedTaxVoucherSummary).details.type,
          ),
          created: taxVoucher.details.associatedTaxVoucherSummary.details.created,
          voucherRef: taxVoucher.details.associatedTaxVoucherSummary.details.voucherRef,
        })
      : createEmptyContent();

  // Process orders according to strategy
  const processedLineItems = processLineItemsByOrder({
    lineItemsByOrder: taxVoucher.details.lineItemsByOrder,
    taxVoucherOptions,
    voucherLetter,
  });

  // Products table
  const productsTable = createProductsTable(processedLineItems, {
    voucherLetter,
    currencyFormatFn: options.currencyFormatFn,
    taxVoucherOptions,
  });

  // Totals summary
  const totalsSummary = createTotalsSummary(taxVoucher, voucherLetter, {
    currencyFormatFn: options.currencyFormatFn,
  });

  // Separator
  const separator = createSeparator();

  // Arca content
  const voucherSummary = createVoucherSummaryContent({
    caeNumber: taxVoucher.details.cae,
    caeExpirationDate: getFormattedCaeExpirationDate(taxVoucher.details.caeFchVto),
    qrCodeUrl: getArcaVoucherQrUrl(taxVoucher),
  });

  const expeditionDate = taxVoucher.details.created;

  const content: Content = [
    headingContent,
    sellerContent,
    buyerContent,
    voucherAssocContent,
    productsTable,
    totalsSummary,
  ];

  if (taxVoucher.details.cae) {
    content.push(separator);
    content.push(voucherSummary);
  }

  const copies =
    taxVoucherOptions.printCopies === 'single'
      ? ['Original']
      : taxVoucherOptions.printCopies === 'double'
        ? ['Original', 'Duplicado']
        : ['Original', 'Duplicado', 'Triplicado'];

  const documentDefinitions = <TDocumentDefinitions>{
    content: copies
      .map((copyIndicator, index) => [
        createCopyIndicator(
          {
            copyIndicator,
            expeditionDate,
          },
          index > 0,
        ),
        ...cloneDeep(content),
      ])
      .flat(),
    pageSize: 'A4',
    pageMargins: [30, 15, 30, 25],
    footer: (currentPage, pageCount) => {
      const pagesPerCopy = pageCount / copies.length;
      const currentPageInCopy = ((currentPage - 1) % pagesPerCopy) + 1;
      return {
        text: `Página ${currentPageInCopy}/${pagesPerCopy}`,
        fontSize: 9,
        bold: false,
        alignment: 'right',
        margin: [30, 10, 30, 5],
      };
    },
  };

  if (options.watermarkText) {
    documentDefinitions.watermark = {
      text: '  ' + options.watermarkText + '  ',
      color: '#000099',
      opacity: 0.1,
      bold: true,
      angle: -45,
    };
  }

  return documentDefinitions;
}

function createCopyIndicator(
  data: { copyIndicator: string; expeditionDate: Date },
  pageBreak: boolean = false,
): ContentColumns {
  return {
    columns: [
      {
        text: data.copyIndicator.toUpperCase(),
        alignment: 'left',
        bold: true,
        margin: 0,
        fontSize: 11,
      },
      {
        text: createInlinedKeyValues('Fecha de emisión', format(data.expeditionDate, 'dd/MM/yyyy')),
        fontSize: 10,
        alignment: 'right',
        margin: 0,
      },
    ],
    margin: [2, 0, 2, 4],
    pageBreak: pageBreak ? 'before' : undefined,
  };
}

function createHeadingContent(data: ArcaHeadingData): ContentTable {
  const documentNameTxt: ContentText & TableCell = {
    text: data.documentName,
    fontSize: 18,
    bold: true,
    marginTop: 8,
    marginLeft: 5,
    alignment: 'left',
    borderColor: ['#666', '#666', '#666', '#666'],
    border: [true, true, true, false],
  };
  const documentTypeStack: ContentStack & TableCell = {
    stack: <ContentText[]>[
      { text: ` ${data.voucherLetter} `, fontSize: 22, lineHeight: 0.9 },
      { text: `Cod. ${data.documentTypeCode}`, fontSize: 9, margin: [3, 0], lineHeight: 0.9 },
    ],
    alignment: 'center',
    marginBottom: 3,
    bold: true,
    border: [true, true, true, false],
    borderColor: ['#666', '#666', '#666', '#666'],
  };
  const documentNumberStack: ContentStack & TableCell = {
    stack: <ContentText[]>[
      {
        text: 'Comprobante Nro.: ',
        fontSize: 9,
        bold: false,
      },
      {
        text: data.documentNumber,
        fontSize: 13,
        bold: true,
      },
    ],
    marginTop: 6,
    marginRight: 5,
    alignment: 'right',
    borderColor: ['#666', '#666', '#666', '#666'],
    border: [false, true, true, false],
  };
  return {
    table: {
      widths: ['*', 'auto', '*'],
      body: [[documentNameTxt, documentTypeStack, documentNumberStack]],
    },
    columnGap: 20,
  };
}

function createVoucherAssocInfo(data: ArcaVoucherAssocInfo): ContentText {
  return {
    text: `COMPROBANTES ASOCIADOS: FACTURA ${data.voucherLetter} Nro.: ${data.voucherRef} | Fecha: ${format(data.created, 'dd/MM/yyyy')}`,
    fontSize: 10,
    bold: true,
    alignment: 'left',
    marginTop: 5,
  };
}

async function createSellerContent(data: ArcaSellerData): Promise<ContentTable> {
  // Left column
  const leftColumnStack = [];

  if (data.logoUrl) {
    try {
      const logoDataUrl = await imageUrlToDataUrl(data.logoUrl);
      leftColumnStack.push({
        image: logoDataUrl,
        fit: [240, 40],
        marginBottom: 8,
      });
    } catch (error) {
      leftColumnStack.push({
        text: data.fantasyName,
        fontSize: 16,
        bold: true,
        margin: [0, 10],
      });
      console.warn('Error loading logo', error);
    }
  }
  leftColumnStack.push(createInlinedKeyValues('Razón social', data.companyName));
  leftColumnStack.push(createInlinedKeyValues('Domicilio comercial', data.address));

  // Right column
  const rightColumnStack = [];

  rightColumnStack.push(createInlinedKeyValues('Condición frente al IVA', data.vatCondition));
  rightColumnStack.push(createInlinedKeyValues('CUIT', data.cuit.toString()));

  if (data.grossIncome) {
    rightColumnStack.push(createInlinedKeyValues('Ingresos brutos', data.grossIncome));
  }
  if (data.operationsStartDate) {
    rightColumnStack.push(
      createInlinedKeyValues(
        'Fecha de inicio de actividades',
        format(data.operationsStartDate, 'dd/MM/yyyy'),
      ),
    );
  }

  return {
    table: {
      widths: ['*', '*'],
      body: [
        [
          {
            stack: leftColumnStack,
            margin: 5,
            alignment: 'center',
            borderColor: ['#666', '#666', '#666', '#666'],
          },
          {
            stack: rightColumnStack,
            margin: 10,
            borderColor: ['#666', '#666', '#666', '#666'],
          },
        ],
      ],
    },
  };
}

function createBuyerContent(data: ArcaBuyerData): ContentColumns {
  const buyerCuit = createInlinedKeyValues(
    getArcaDocumentTypeName(data.documentType),
    data.documentNumber || '-',
  );
  const buyerCondition = createInlinedKeyValues('Condición frente al IVA', data.vatCondition);
  const buyerName = createInlinedKeyValues('Razón social / Nombre', data.name || '-');
  const buyerDomicilio = createInlinedKeyValues('Domicilio comercial', data.address || '-');

  return {
    columns: [
      { stack: [buyerCuit, buyerCondition], width: '*' },
      { stack: [buyerName, buyerDomicilio], width: '*' },
    ],
    marginTop: 15,
  };
}

function createProductsTable(
  processedLineItems: ArcaLineItemsByOrder[],
  options: {
    voucherLetter: ArcaVoucherLetter;
    taxVoucherOptions: TaxVoucherOptions;
    currencyFormatFn: (amount: number) => string;
  },
): ContentTable {
  const tableBody = [];

  const renderingStrategy = determineRenderingStrategy(
    processedLineItems,
    options.taxVoucherOptions,
  );

  // Create header row
  if (options.voucherLetter === 'A') {
    tableBody.push([
      createTableHeadingCell('Cant.', 'center'),
      createTableHeadingCell('Producto', 'left'),
      createTableHeadingCell('Precio unit.', 'right'),
      createTableHeadingCell('Subtotal', 'right'),
      createTableHeadingCell('IVA %', 'center'),
      createTableHeadingCell('Subtotal c/IVA', 'right'),
    ]);
  } else {
    tableBody.push([
      createTableHeadingCell('Cant.', 'center'),
      createTableHeadingCell('Producto', 'left'),
      createTableHeadingCell('Precio unit.', 'right'),
      createTableHeadingCell('Subtotal', 'right'),
    ]);
  }

  // Process orders based on rendering strategy
  processedLineItems.forEach((order, orderIndex) => {
    // Add order header for multiple orders without grouping
    if (renderingStrategy === 'multiple-orders-with-headers' && processedLineItems.length > 1) {
      const colSpan = options.voucherLetter === 'A' ? 6 : 4;
      tableBody.push([
        {
          text: `Pedido #${order.orderId}`,
          bold: true,
          fillColor: '#f0f0f0',
          margin: [4, 6, 4, 6],
          fontSize: 10,
          alignment: 'left',
          colSpan: colSpan,
        },
        ...Array(colSpan - 1).fill({}), // Empty cells for colSpan
      ]);
    }

    // Add product rows
    order.lineItems.forEach(product => {
      if (options.voucherLetter === 'A') {
        const aliquotLabel = product.aliquot === 'exempt' ? 'Exento' : `${product.aliquot}%`;

        tableBody.push([
          createTableDataCell(product.quantity, 'center'),
          createTableDataCell(product.name, 'left'),
          createTableDataCell(options.currencyFormatFn(product.unitPrice), 'right'),
          createTableDataCell(options.currencyFormatFn(product.aliquotBaseAmount), 'right'),
          createTableDataCell(aliquotLabel, 'center'),
          createTableDataCell(options.currencyFormatFn(product.totalAmount), 'right'),
        ]);
      } else {
        tableBody.push([
          createTableDataCell(product.quantity, 'center'),
          createTableDataCell(product.name, 'left'),
          createTableDataCell(options.currencyFormatFn(product.unitPrice), 'right'),
          createTableDataCell(options.currencyFormatFn(product.totalAmount), 'right'),
        ]);
      }
    });
  });

  return {
    table: {
      headerRows: 1,
      widths:
        options.voucherLetter === 'A'
          ? ['auto', '*', 'auto', 'auto', 'auto', 'auto']
          : ['auto', '*', 'auto', 'auto'],
      dontBreakRows: true,
      body: tableBody,
    },
    marginTop: 10,
  };
}

function createTotalsSummary(
  taxVoucher: TaxVoucherArca,
  voucherLetter: ArcaVoucherLetter,
  options: {
    currencyFormatFn: (amount: number) => string;
  },
): ContentColumns {
  if (voucherLetter === 'A') {
    const ivaRows: { label: string; amount: number }[] = [];

    taxVoucher.details.voucherTotals.vatDetails?.forEach(vatDetail => {
      ivaRows.push({
        label: `IVA ${vatDetail.aliquot}%:`,
        amount: vatDetail.vatAmount,
      });
    });

    const leftColumnContent = [createSubotalSummaryText('Importe Neto Gravado:')];

    if (taxVoucher.details.voucherTotals.exemptAmount > 0) {
      leftColumnContent.push(createSubotalSummaryText('IVA Exento:'));
    }
    leftColumnContent.push(...ivaRows.map(row => createSubotalSummaryText(row.label)));
    leftColumnContent.push(createTotalSummaryText('Importe total:'));

    const rightColumnContent = [
      createSubotalSummaryText(
        options.currencyFormatFn(taxVoucher.details.voucherTotals.netAmount),
      ),
    ];
    if (taxVoucher.details.voucherTotals.exemptAmount > 0) {
      rightColumnContent.push(
        createSubotalSummaryText(
          options.currencyFormatFn(taxVoucher.details.voucherTotals.exemptAmount),
        ),
      );
    }
    rightColumnContent.push(
      ...ivaRows.map(row => createSubotalSummaryText(options.currencyFormatFn(row.amount))),
    );
    rightColumnContent.push(
      createTotalSummaryText(
        options.currencyFormatFn(taxVoucher.details.voucherTotals.totalAmount),
      ),
    );

    return {
      columns: [
        {
          width: '*',
          stack: leftColumnContent,
          alignment: 'right',
        },
        {
          width: 'auto',
          stack: rightColumnContent,
          alignment: 'right',
        },
      ],
      columnGap: 40,
      marginTop: 15,
    };
  }
  const leftContent =
    voucherLetter === 'B'
      ? {
          fontSize: 9,
          table: {
            widths: ['auto', '*'],
            body: [
              [
                createTableHeadingCell(
                  'Régimen de Transparencia Fiscal al Consumidor (Ley 27.743)',
                  'left',
                  { fontSize: 8, margin: [4, 2] },
                ),
                createTableHeadingCell('Importe', 'right', { fontSize: 8, margin: [4, 2] }),
              ],
              [
                createTableDataCell('IVA Contenido', 'left', {
                  fontSize: 8,
                  margin: [4, 2, 4, 0],
                }),
                createTableDataCell(
                  options.currencyFormatFn(taxVoucher.details.voucherTotals.vatAmount),
                  'right',
                  {
                    fontSize: 8,
                    margin: [4, 2, 4, 0],
                  },
                ),
              ],
              [
                createTableDataCell('Otros Impuestos Nacionales Indirectos', 'left', {
                  fontSize: 8,
                  margin: [4, 0],
                }),
                createTableDataCell(0, 'right', { fontSize: 8, margin: [4, 0] }),
              ],
            ],
          },
          layout: 'noBorders',
          marginTop: 15,
        }
      : createEmptyContent();

  const subtotalAmount =
    taxVoucher.details.voucherTotals.totalAmount - taxVoucher.details.voucherTotals.tributesAmount;
  const rightContent = {
    columns: [
      {
        width: '*',
        stack: [
          createSubotalSummaryText('Subtotal:'),
          createSubotalSummaryText('Importe otros tributos:'),
          createTotalSummaryText('Importe total:'),
        ],
        alignment: 'right',
      },
      {
        width: 'auto',
        stack: [
          createSubotalSummaryText(options.currencyFormatFn(subtotalAmount)),
          createSubotalSummaryText(
            options.currencyFormatFn(taxVoucher.details.voucherTotals.tributesAmount),
          ),
          createTotalSummaryText(
            options.currencyFormatFn(taxVoucher.details.voucherTotals.totalAmount),
          ),
        ],
        alignment: 'right',
      },
    ],
    columnGap: 40,
    marginTop: 15,
  };

  return {
    columns: [<Column>{ width: 'auto', ...leftContent }, <Column>{ width: '*', ...rightContent }],
    columnGap: 20,
  };
}
function createVoucherSummaryContent(data: {
  caeNumber: string;
  caeExpirationDate: string;
  qrCodeUrl: string;
}): ContentColumns {
  return {
    columns: [
      { width: 'auto', qr: data.qrCodeUrl, fit: 80 },
      {
        width: '*',
        stack: [
          {
            text: 'Comprobante autorizado',
            fontSize: 10,
            marginBottom: 8,
          },
          createInlinedKeyValues('CAE Nro', data.caeNumber),
          createInlinedKeyValues('Fecha vencimiento CAE', data.caeExpirationDate),
        ],
        alignment: 'left',
        marginTop: 2,
      },
    ],
    marginTop: 15,
    columnGap: 20,
  };
}

function createSubotalSummaryText(text: string | number): ContentText {
  return { text, fontSize: 9, bold: false, lineHeight: 1.2 };
}
function createTotalSummaryText(text: string | number): ContentText {
  return { text, fontSize: 13, bold: true, lineHeight: 1.2 };
}

function createStackedKeyValues(key: string, value: string): ContentStack {
  return {
    stack: <ContentText[]>[
      { text: `${key}:`, bold: true, fontSize: 9, lineHeight: 1.2 },
      { text: value, bold: false, fontSize: 11 },
    ],
    marginBottom: 5,
  };
}
function createInlinedKeyValues(key: string, value: string | number): ContentText {
  return {
    text: [
      { text: `${key}: `, bold: true, fontSize: 10 },
      { text: value, bold: false, fontSize: 10 },
    ],
    marginBottom: 5,
  };
}
function createTableHeadingCell(
  text: string,
  alignment: 'left' | 'right' | 'center',
  styleOverrides?: Partial<TableCell>,
): TableCell {
  return {
    text,
    bold: true,
    fillColor: '#ccc',
    margin: 2,
    fontSize: 10,
    alignment,
    ...styleOverrides,
  };
}
function createTableDataCell(
  text: string | number,
  alignment: 'left' | 'right' | 'center',
  styleOverrides?: Partial<TableCell>,
): TableCell {
  return { text, bold: false, margin: 2, fontSize: 9, alignment, ...styleOverrides };
}
function createSeparator(): ContentTable {
  return {
    table: {
      widths: ['*'],
      body: [[''], ['']],
      headerRows: 1,
    },
    layout: 'headerLineOnly',
    marginTop: 20,
  };
}
function createEmptyContent(): ContentText {
  return { text: '', lineHeight: 0, fontSize: 0 };
}
